/**
 * Helpers para gerenciar sessões salvas de usuários
 * Suporta localStorage (lembrar de mim) e sessionStorage (sessão temporária)
 */

import { SavedSession, SaveSessionParams } from '@/types/session';

const STORAGE_KEY = '@TROIA-V3:savedSessions';

/**
 * Carrega todas as sessões salvas do localStorage
 */
export const loadSavedSessions = (): SavedSession[] => {
  try {
    const savedSessionsStr = localStorage.getItem(STORAGE_KEY);
    if (savedSessionsStr) {
      const sessions = JSON.parse(savedSessionsStr) as SavedSession[];
      // Ordenar por último login (mais recente primeiro)
      return sessions.sort((a, b) =>
        new Date(b.lastLoginAt).getTime() - new Date(a.lastLoginAt).getTime()
      );
    }
  } catch (error) {
    console.error('Error loading saved sessions:', error);
  }
  return [];
};

/**
 * Salva uma nova sessão ou atualiza uma existente
 * @param sessionData - Dados da sessão a ser salva
 * @param rememberMe - Se true, salva permanentemente; se false, apenas sessionStorage
 */
export const saveSession = (
  sessionData: SaveSessionParams,
  rememberMe: boolean
): SavedSession[] => {
  try {
    const newSession: SavedSession = {
      ...sessionData,
      rememberMe,
      lastLoginAt: new Date().toISOString(),
    };

    const existingSessions = loadSavedSessions();

    // Verificar se sessão já existe (por token ou email)
    const existingIndex = existingSessions.findIndex(
      s => s.token === newSession.token || s.email === newSession.email
    );

    let updatedSessions: SavedSession[];

    if (existingIndex >= 0) {
      // Atualizar sessão existente
      updatedSessions = [...existingSessions];
      updatedSessions[existingIndex] = {
        ...updatedSessions[existingIndex],
        ...newSession,
      };
    } else {
      // Adicionar nova sessão
      updatedSessions = [newSession, ...existingSessions];
    }

    // Salvar no localStorage se rememberMe = true
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSessions));
    } else {
      // Se não marcar "lembrar de mim", salvar apenas as outras sessões
      const otherSessions = updatedSessions.filter(s => s.email !== newSession.email);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(otherSessions));

      // Salvar sessão atual apenas no sessionStorage
      sessionStorage.setItem('@TROIA-V3:currentSession', JSON.stringify(newSession));
    }

    return updatedSessions;
  } catch (error) {
    console.error('Error saving session:', error);
    return loadSavedSessions();
  }
};

/**
 * Remove uma sessão específica da lista
 */
export const removeSession = (sessionId: string): SavedSession[] => {
  try {
    const sessions = loadSavedSessions();
    const updatedSessions = sessions.filter(s => s.id !== sessionId);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSessions));

    // Também remover do sessionStorage se for a sessão atual
    const currentSessionStr = sessionStorage.getItem('@TROIA-V3:currentSession');
    if (currentSessionStr) {
      const currentSession = JSON.parse(currentSessionStr) as SavedSession;
      if (currentSession.id === sessionId) {
        sessionStorage.removeItem('@TROIA-V3:currentSession');
      }
    }

    return updatedSessions;
  } catch (error) {
    console.error('Error removing session:', error);
    return loadSavedSessions();
  }
};

/**
 * Atualiza o timestamp de último login de uma sessão
 */
export const updateLastLogin = (sessionId: string): SavedSession[] => {
  try {
    const sessions = loadSavedSessions();
    const sessionIndex = sessions.findIndex(s => s.id === sessionId);

    if (sessionIndex >= 0) {
      sessions[sessionIndex].lastLoginAt = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    }

    return sessions;
  } catch (error) {
    console.error('Error updating last login:', error);
    return loadSavedSessions();
  }
};

/**
 * Limpa todas as sessões salvas
 */
export const clearAllSessions = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('@TROIA-V3:currentSession');
  } catch (error) {
    console.error('Error clearing sessions:', error);
  }
};

/**
 * Obtém a sessão atual (pode estar no localStorage ou sessionStorage)
 */
export const getCurrentSession = (): SavedSession | null => {
  try {
    // Tentar pegar do sessionStorage primeiro (sessão temporária)
    const sessionStr = sessionStorage.getItem('@TROIA-V3:currentSession');
    if (sessionStr) {
      return JSON.parse(sessionStr) as SavedSession;
    }

    // Se não tiver no sessionStorage, pegar a mais recente do localStorage
    const sessions = loadSavedSessions();
    return sessions.length > 0 ? sessions[0] : null;
  } catch (error) {
    console.error('Error getting current session:', error);
    return null;
  }
};

/**
 * Obtém iniciais do nome do usuário para fallback de avatar
 */
export const getInitials = (name: string): string => {
  if (!name) return 'U';

  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};
